import { Button, Card, Descriptions, Form, Input, InputNumber, Select, Space, Statistic, Table, Tag, message } from 'antd';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import api from '@/services/api.service';
import { useAuthStore } from '@/store/authStore';
import { formatMoney } from '@/utils/format';
import EducationPageChrome from '@/pages/education/components/EducationPageChrome';

type Ledger = {
  outstanding: number;
  payer?: { customer_id?: string; customer_name?: string; customer_code?: string } | null;
  demands: Array<{
    id: string;
    fee_head_code?: string;
    fee_head_name?: string;
    period_key: string;
    amount: number;
    status: string;
    sales_invoice_id?: string | null;
    receipt_voucher_id?: string | null;
  }>;
};

export default function StudentFeeLedgerPage() {
  const { t } = useTranslation('education');
  const { studentId = '' } = useParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const user = useAuthStore((s) => s.user);
  const canConcession = Boolean(
    user?.permissions?.includes('education.fee.concession') || user?.permissions?.includes('education.admin')
  );
  const canDemand = Boolean(
    user?.permissions?.includes('education.fee.demand') || user?.permissions?.includes('education.admin')
  );

  const query = useQuery({
    queryKey: ['education-fee-ledger', studentId],
    enabled: Boolean(studentId),
    queryFn: () => api.get<Ledger>(`/education/fees/students/${studentId}/ledger`),
  });
  const ledger = query.data?.data;

  const ensurePayer = useMutation({
    mutationFn: () => api.post(`/education/fees/students/${studentId}/payer`, {}),
    onSuccess: () => {
      message.success(t('feePayerLinked'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-ledger', studentId] });
    },
  });

  const concession = useMutation({
    mutationFn: (values: { demand_id: string; amount: number; reason: string }) =>
      api.post('/education/fees/concessions', values),
    onSuccess: () => {
      message.success(t('feeConcessionSubmitted'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-ledger', studentId] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const invoiceDemand = useMutation({
    mutationFn: (demandId: string) => api.post(`/education/fees/demands/${demandId}/invoice`, {}),
    onSuccess: () => {
      message.success(t('feeInvoiceCreated'));
      queryClient.invalidateQueries({ queryKey: ['education-fee-ledger', studentId] });
    },
    onError: (error: { response?: { data?: { message?: string } } }) =>
      message.error(error.response?.data?.message || t('unavailable', { status: '' })),
  });

  const openDemands = (ledger?.demands ?? []).filter((d) => !d.receipt_voucher_id);

  return (
    <EducationPageChrome
      title={t('studentFeeLedger')}
      description={t('studentFeeLedgerIntro')}
      extra={
        <Space>
          <Button onClick={() => navigate(`/education/students/${studentId}`)}>{t('backToStudent')}</Button>
          <Button onClick={() => navigate('/education/fees/outstanding')}>{t('feeOutstanding')}</Button>
          <Button onClick={() => navigate('/education/fees/demands')}>{t('feeDemands')}</Button>
        </Space>
      }
    >
      <Space direction="vertical" size="middle" style={{ width: '100%' }}>
        <Card size="small" loading={query.isLoading}>
          <Space size="large" wrap>
            <Statistic title={t('outstanding')} value={ledger?.outstanding ?? 0} precision={2} />
            <Descriptions size="small" column={1}>
              <Descriptions.Item label={t('payer')}>
                {ledger?.payer
                  ? `${ledger.payer.customer_code || ''} — ${ledger.payer.customer_name || ledger.payer.customer_id}`
                  : t('noPayer')}
              </Descriptions.Item>
            </Descriptions>
            {!ledger?.payer ? (
              <Button type="primary" loading={ensurePayer.isPending} onClick={() => ensurePayer.mutate()}>
                {t('ensurePayer')}
              </Button>
            ) : null}
          </Space>
        </Card>

        <Card size="small" title={t('feeDemands')}>
          <Table
            rowKey="id"
            size="small"
            pagination={false}
            dataSource={ledger?.demands ?? []}
            columns={[
              {
                title: t('feeHead'),
                render: (_: unknown, row) => `${row.fee_head_code || ''} — ${row.fee_head_name || ''}`,
              },
              { title: t('periodKey'), dataIndex: 'period_key', width: 110 },
              { title: t('amount'), dataIndex: 'amount', width: 100 },
              {
                title: t('status'),
                dataIndex: 'status',
                width: 100,
                render: (v: string) => <Tag>{v}</Tag>,
              },
              {
                title: t('invoice'),
                dataIndex: 'sales_invoice_id',
                ellipsis: true,
                render: (v) => v || '—',
              },
              {
                title: t('receipt'),
                dataIndex: 'receipt_voucher_id',
                ellipsis: true,
                render: (v) => v || '—',
              },
              {
                title: t('actions'),
                width: 140,
                render: (_: unknown, row) =>
                  canDemand && !row.sales_invoice_id ? (
                    <Button
                      size="small"
                      type="primary"
                      loading={invoiceDemand.isPending}
                      onClick={() => invoiceDemand.mutate(row.id)}
                    >
                      {t('createInvoice')}
                    </Button>
                  ) : null,
              },
            ]}
          />
        </Card>

        {canConcession ? (
          <Card size="small" title={t('submitFeeConcession')}>
            <Form layout="vertical" style={{ maxWidth: 520 }} onFinish={(values) => concession.mutate(values)}>
              <Form.Item name="demand_id" label={t('feeDemand')} rules={[{ required: true }]}>
                <Select
                  showSearch
                  optionFilterProp="label"
                  options={openDemands.map((d) => ({
                    value: d.id,
                    label: `${d.fee_head_code || ''} ${d.period_key} — ${formatMoney(Number(d.amount), { currency: 'INR', display: 'plain', locale: 'en-IN' })}`,
                  }))}
                  placeholder={t('demandIdHint')}
                />
              </Form.Item>
              <Form.Item name="amount" label={t('amount')} rules={[{ required: true }]}>
                <InputNumber min={0.01} style={{ width: '100%' }} />
              </Form.Item>
              <Form.Item name="reason" label={t('reason')} rules={[{ required: true }]}>
                <Input.TextArea rows={3} />
              </Form.Item>
              <Button type="primary" htmlType="submit" loading={concession.isPending}>
                {t('submitForApproval')}
              </Button>
            </Form>
          </Card>
        ) : null}
      </Space>
    </EducationPageChrome>
  );
}
